import { useCallback, useRef } from 'react';
import { useCurrentUser } from '../auth/AuthProvider.js';
import { useSessionValue } from './preferences.js';

/**
 * The filters of a list (11/10/2026), remembered while the browser stays open:
 * coming back from a document, the list is as it was left. `changed` says
 * which filter differs from its default, to show it and offer to reset all.
 */
export function useListFilters<T extends Record<string, unknown>>(
  id: string,
  defaults: T,
): {
  filters: T;
  setFilter: <K extends keyof T>(key: K, value: T[K]) => void;
  reset: () => void;
  changed: boolean;
  /** «filter-changed» for a filter not at its default, to colour it. */
  changedClass: (key: keyof T, base?: string) => string;
} {
  const user = useCurrentUser();
  const [stored, setStored] = useSessionValue<Partial<T>>(`filters.${id}.${user.email}`, {});
  const filters = { ...defaults, ...stored };
  // The latest filters, so that two changes in the same click both stay (1.70):
  // the year and then the month reset used to lose the year.
  const latest = useRef(stored);
  latest.current = stored;
  const save = useCallback(
    (next: Partial<T>) => {
      latest.current = next;
      setStored(next);
    },
    [setStored],
  );
  const setFilter = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => save({ ...latest.current, [key]: value }),
    [save],
  );
  const differs = (key: keyof T) => JSON.stringify(filters[key]) !== JSON.stringify(defaults[key]);
  return {
    filters,
    setFilter,
    reset: () => save({}),
    changed: Object.keys(defaults).some((key) => differs(key)),
    changedClass: (key, base = 'compact-select') =>
      differs(key) ? `${base} filter-changed` : base,
  };
}
